import { isCityCommand } from "../cityCommand/cityVerbs";
import { commandRefusal, spendWill } from "../court/king";
import { without } from "../fields";
import { type AxialCoord, hexEquals, hexKey, hexNeighbors } from "../hex";
import { findHex, hexTopology, neighbourPositions, unitById } from "../run/board";
import { isHeldUnit, isSeatUnit } from "../run/seats";
import { type Side, sameOwner, sideOf } from "../run/sides";
import { ordersFreely } from "../soldier/work";
import type { FactionId, HexOwner, HexState, Role, RunState, Unit } from "../types/state";
import { isBroken } from "./morale";
import { replaceUnit } from "./unit";

/**
 * Roads and orders: which ground a unit may cross, the road search and its first step, the flood a
 * spawn and a unit's reach walk, the order lock, giving and cancelling an order.
 */

export interface Ground {
  readonly coord: AxialCoord;
  readonly owner: HexOwner;
  readonly razed: boolean;
  readonly walls: number;
}

/** A hex of no ally behind walls: `side` stops before it and grinds them, it never steps in (K6: an ally's walls are its own). */
export function walledAgainst(hex: Ground, side: Side): boolean {
  return !side.has(hex.owner) && hex.walls > 0;
}

/**
 * Whether `side` may walk into a hex: never razed, and a foreign hex behind walls only when
 * it is the destination itself, where the walker stops beside it and grinds the walls.
 */
function passable(hex: Ground, side: Side, destination: AxialCoord): boolean {
  if (hex.razed) {
    return false;
  }
  return side.has(hex.owner) || hex.walls === 0 || hexEquals(hex.coord, destination);
}

/**
 * Shortest walk from `from` to `to` for a walker of `side`: breadth-first over passable hexes, never
 * through `blocked` keys (occupied hexes when the caller wants a detour). With `throughWalls`
 * a walled foreign hex counts as a step too: the walker stops before it and grinds it, so a
 * wall line is a delay, never a barrier. Neighbours are expanded in insertion order of
 * `hexes`, so equal-length routes always pick the lower-index neighbour and a replay walks
 * the same road. Undefined when unreachable or from equals to.
 */
export function walk(
  hexes: readonly Ground[],
  side: Side,
  from: AxialCoord,
  to: AxialCoord,
  blocked: ReadonlySet<string>,
  throughWalls: boolean,
): AxialCoord[] | undefined {
  if (hexEquals(from, to)) {
    return undefined;
  }
  const { index, neighbours } = hexTopology(hexes);
  const target = index.get(hexKey(to));
  if (target === undefined) {
    return undefined;
  }
  const start = index.get(hexKey(from));
  const FROM = -1;
  const UNSEEN = -2;
  // parent[position]: UNSEEN, FROM (reached straight from `from`), or the position it was reached from.
  const parent = new Int32Array(hexes.length).fill(UNSEEN);
  if (start !== undefined) {
    parent[start] = FROM;
  }
  const queue: number[] = [FROM];
  for (let head = 0; head < queue.length; head += 1) {
    const current = queue[head] ?? FROM;
    const around =
      current === FROM
        ? start === undefined
          ? neighbourPositions(index, from)
          : (neighbours[start] ?? [])
        : (neighbours[current] ?? []);
    for (const next of around) {
      const hex = hexes[next];
      if (!hex || parent[next] !== UNSEEN) {
        continue;
      }
      const isTarget = next === target;
      const open = throughWalls ? !hex.razed : passable(hex, side, to);
      if (!open || (!isTarget && blocked.size > 0 && blocked.has(hexKey(hex.coord)))) {
        continue;
      }
      parent[next] = current;
      if (isTarget) {
        const route: AxialCoord[] = [];
        for (let step = next; step !== FROM; step = parent[step] ?? FROM) {
          const stepHex = hexes[step];
          if (stepHex) {
            route.push(stepHex.coord);
          }
        }
        route.push(from);
        return route.reverse();
      }
      // A walled foreign hex is a place to stop at, never a place to walk through.
      if (throughWalls || side.has(hex.owner) || hex.walls === 0) {
        queue.push(next);
      }
    }
  }
  return undefined;
}

/** No hex blocked: the road a walker takes ignoring who stands where. */
const NO_BLOCKS: ReadonlySet<string> = new Set();

/** The open road first; failing that, the road that grinds through walls. */
export function road(
  hexes: readonly Ground[],
  side: Side,
  from: AxialCoord,
  to: AxialCoord,
  blocked: ReadonlySet<string> = NO_BLOCKS,
): AxialCoord[] | undefined {
  return walk(hexes, side, from, to, blocked, false) ?? walk(hexes, side, from, to, blocked, true);
}

/** The first hex of the road from `from` to `to` (`road`, nobody blocking); undefined without one. */
export function nextStepOf(
  hexes: readonly Ground[],
  side: Side,
  from: AxialCoord,
  to: AxialCoord,
): AxialCoord | undefined {
  return road(hexes, side, from, to)?.[1];
}

/** The road a unit would take to `to`, ignoring who stands where; undefined when there is none. */
export function routeUnit(state: RunState, unit: Unit, to: AxialCoord): AxialCoord[] | undefined {
  return road(state.hexes, sideOf(state.rules, unit.owner), unit.coord, to);
}

/**
 * Every hex a unit could be sent to: all unrazed land connected to where it stands, walls
 * included (they are ground down on the way). Keys via hexKey; the unit's own hex excluded.
 * The renderer dims the rest.
 */
export function unitReach(state: RunState, unit: Unit): Set<string> {
  const reach = new Set<string>();
  for (const coord of floodFrom(state, unit.coord, (hex) => !hex.razed)) {
    if (!hexEquals(coord, unit.coord)) {
      reach.add(hexKey(coord));
    }
  }
  return reach;
}

/**
 * Every hex a breadth-first walk from `start` reaches over the hexes `admits` lets in, `start`
 * first, each in the order it is reached; neighbours in `hexNeighbors` order, so a replay walks
 * the same. Lazy: a caller that stops at the first hex it wants walks no further.
 */
export function* floodFrom(
  state: RunState,
  start: AxialCoord,
  admits: (hex: HexState) => boolean,
): Generator<AxialCoord> {
  const seen = new Set<string>([hexKey(start)]);
  const queue: AxialCoord[] = [start];
  for (let head = 0; head < queue.length; head += 1) {
    const current = queue[head] ?? start;
    yield current;
    for (const next of hexNeighbors(current)) {
      const key = hexKey(next);
      const hex = findHex(state, next);
      if (hex && admits(hex) && !seen.has(key)) {
        seen.add(key);
        queue.push(next);
      }
    }
  }
}

/**
 * An order is a commitment: it can neither be replaced nor cancelled until the unit's next
 * move step has passed (moved or not, so a blocked unit never holds a dead order for good).
 * Not in a city-command kingdom (CC1), nor for the human's soldier with `freeOrders` (CC5): there
 * the last order wins.
 */
export function isOrderLocked(state: RunState, unit: Unit): boolean {
  if (
    unit.destination === undefined ||
    isCityCommand(state, unit.owner) ||
    ordersFreely(state, unit)
  ) {
    return false;
  }
  const interval = state.rules.moveIntervalTicks;
  const nextStepTick = (Math.floor(unit.orderedTick / interval) + 1) * interval;
  return state.tick < nextStepTick;
}

/**
 * Send a unit somewhere: the faction's unit walks there over the coming move steps and
 * fights whatever it meets. The same destination again is a no-op, save the seat's tap that sets
 * its soldier to work there (CC5); rejected (same object)
 * when the run is over, the unit is not the faction's, its current order is still locked or
 * no road exists. The King never walks a commander by hand (he aims it, `aimCommander`), and
 * his order to a squad member takes the soldier back from its commander.
 */
export function orderUnit(
  state: RunState,
  faction: FactionId,
  unitId: number,
  to: AxialCoord,
  issuer: Role = "king",
): RunState {
  if (state.status !== "running") {
    return state;
  }
  const unit = unitById(state, unitId);
  if (!unit || !sameOwner(unit.owner, faction) || (unit.commander && issuer === "king")) {
    return state;
  }
  // M1, D8: a held seat's soldier walks where its human says and nowhere else.
  if (isHeldUnit(state, unitId) && issuer !== "soldier") {
    return state;
  }
  // CC5: the seat's walk with empty hands works its road and its patch; any other ends the work.
  const works =
    issuer === "soldier" &&
    isSeatUnit(state, unitId) &&
    state.rules.soldier?.workRoute !== undefined &&
    unit.carry === undefined;
  const setsToWork = works && (unit.works === undefined || !hexEquals(unit.works, to));
  if (unit.destination && hexEquals(unit.destination, to) && !setsToWork) {
    return state;
  }
  if (
    isBroken(state, unit) ||
    isOrderLocked(state, unit) ||
    commandRefusal(state, faction, unit.coord, "order", issuer) !== undefined ||
    !routeUnit(state, unit, to)
  ) {
    return state;
  }
  // Any new order ends an errand to a mayor's office (A32); the Will it cost stays spent.
  const own = without(unit, "squadOf", "governs", "works");
  const kept =
    issuer === "king" || unit.squadOf === undefined ? own : { ...own, squadOf: unit.squadOf };
  const ordered = { ...kept, destination: to, orderedTick: state.tick, orderedBy: issuer };
  return replaceUnit(
    spendWill(state, faction, "order", issuer),
    unit,
    works ? { ...ordered, works: to } : ordered,
  );
}

/**
 * The unit stops where it stands, and the human's soldier ends its work (CC5). Same object back
 * when it was already idle, its order is still locked or it is not the faction's. The King's stop
 * needs reach; a delegate's does not.
 */
export function cancelUnitOrder(
  state: RunState,
  faction: FactionId,
  unitId: number,
  issuer: Role = "king",
): RunState {
  const unit = unitById(state, unitId);
  if (
    !unit ||
    !sameOwner(unit.owner, faction) ||
    (unit.destination === undefined && unit.works === undefined) ||
    isBroken(state, unit) ||
    isOrderLocked(state, unit) ||
    commandRefusal(state, faction, unit.coord, "stop", issuer) !== undefined
  ) {
    return state;
  }
  return replaceUnit(state, unit, without(unit, "destination", "governs", "works"));
}
